import { isDigit, javaTrim } from './javaCompat';

/**
 * Linear barcode encoder for TfrxBarCodeView (port of Fr3Barcode).
 *
 * The engine turns the module pattern into FILL ops, so every emitter draws
 * the barcode without knowing anything about symbologies. Covers EAN13, EAN8,
 * UPC-A, Code39, Code128 (B/C automatic) and 2/5 interleaved.
 *
 * Encoded: { widths: number[] (bar/space modules, starting with a bar),
 * guards: [start, length][], text, leadingDigit, leftHalf, rightHalf, ean }.
 */

const newEncoded = () => ({
    widths: [],
    guards: [],
    text: '',
    leadingDigit: '',
    leftHalf: '',
    rightHalf: '',
    ean: false,
});

export const totalModules = (e) => e.widths.reduce((n, w) => n + w, 0);

const norm = (barType) => (barType == null ? '' : javaTrim(barType).toLowerCase());

const SUPPORTED = new Set([
    'bccodeean13', 'bccodeean8', 'bccodeupc_a', 'bccode39', 'bccode39extended',
    'bccode128', 'bccode128a', 'bccode128b', 'bccode128c', 'bccode_2_5_interleaved',
]);

/** True when encode() can draw this FastReport bar type. */
export const supports = (barType) => SUPPORTED.has(norm(barType));

/** The symbol, or null when the value cannot be encoded in this type. */
export const encode = (barType, value, calcCheckSum) => {
    const v = value == null ? '' : javaTrim(value);
    switch (norm(barType)) {
        case 'bccodeean13': return ean(v, 13, calcCheckSum);
        case 'bccodeean8': return ean(v, 8, calcCheckSum);
        case 'bccodeupc_a': return ean(v, 12, calcCheckSum);
        case 'bccode39':
        case 'bccode39extended': return code39(v, calcCheckSum);
        case 'bccode128':
        case 'bccode128a':
        case 'bccode128b':
        case 'bccode128c': return code128(v);
        case 'bccode_2_5_interleaved': return itf(v, calcCheckSum);
        default: return null;
    }
};

// -- EAN / UPC -------------------------------------------------------------------

const L_CODES = ['0001101', '0011001', '0010011', '0111101', '0100011',
    '0110001', '0101111', '0111011', '0110111', '0001011'];
const G_CODES = ['0100111', '0110011', '0011011', '0100001', '0011101',
    '0111001', '0000101', '0010001', '0001001', '0010111'];
const R_CODES = ['1110010', '1100110', '1101100', '1000010', '1011100',
    '1001110', '1010000', '1000100', '1001000', '1110100'];
/** EAN13 parity of the six left digits, selected by the first digit. */
const PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG',
    'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL'];

const digitAt = (s, i) => s.charCodeAt(i) - 48;

/**
 * FastReport pads short input with leading zeros, cuts long input to length,
 * and with CalcCheckSum replaces the last digit by the computed one.
 */
const ean = (value, length, calcCheckSum) => {
    let digits = value.replace(/[^0-9]/g, '');
    const body = length - 1;
    if (calcCheckSum) {
        digits = fit(digits, body);
        digits = digits + eanCheck(digits);
    } else {
        digits = fit(digits, length);
    }

    let bits = '';
    const e = newEncoded();
    e.ean = true;
    const guardRanges = [];
    if (length === 13) {
        const parity = PARITY[digitAt(digits, 0)];
        guardRanges.push([bits.length, 3]);
        bits += '101';
        for (let i = 1; i <= 6; i++) {
            const d = digitAt(digits, i);
            bits += parity[i - 1] === 'L' ? L_CODES[d] : G_CODES[d];
        }
        guardRanges.push([bits.length, 5]);
        bits += '01010';
        for (let i = 7; i <= 12; i++) bits += R_CODES[digitAt(digits, i)];
        guardRanges.push([bits.length, 3]);
        bits += '101';
        e.leadingDigit = digits.substring(0, 1);
        e.leftHalf = digits.substring(1, 7);
        e.rightHalf = digits.substring(7);
    } else {
        // EAN8 (4+4) and UPC-A (6+6): left half in L codes, right in R codes.
        const half = Math.trunc(length / 2);
        guardRanges.push([bits.length, 3]);
        bits += '101';
        for (let i = 0; i < half; i++) bits += L_CODES[digitAt(digits, i)];
        guardRanges.push([bits.length, 5]);
        bits += '01010';
        for (let i = half; i < length; i++) bits += R_CODES[digitAt(digits, i)];
        guardRanges.push([bits.length, 3]);
        bits += '101';
        e.leftHalf = digits.substring(0, half);
        e.rightHalf = digits.substring(half);
    }
    e.text = digits;
    bitsToWidths(bits, e.widths);
    guardRanges.forEach((g) => e.guards.push(g));
    return e;
};

const fit = (digits, len) => {
    if (digits.length > len) return digits.substring(0, len);
    return '0'.repeat(len - digits.length) + digits;
};

const eanCheck = (body) => {
    let sum = 0;
    // Weights 3,1,3,... counted from the rightmost body digit.
    for (let i = body.length - 1, w = 3; i >= 0; i--, w = (w === 3 ? 1 : 3)) {
        sum += digitAt(body, i) * w;
    }
    return String.fromCharCode(48 + (10 - (sum % 10)) % 10);
};

// -- Code39 ----------------------------------------------------------------------

const CODE39_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-. $/+%*';
/** 9 elements per character (bar, space, bar...), 1 = wide. */
const CODE39 = [
    '000110100', '100100001', '001100001', '101100000', '000110001',
    '100110000', '001110000', '000100101', '100100100', '001100100',
    '100001001', '001001001', '101001000', '000011001', '100011000',
    '001011000', '000001101', '100001100', '001001100', '000011100',
    '100000011', '001000011', '101000010', '000010011', '100010010',
    '001010010', '000000111', '100000110', '001000110', '000010110',
    '110000001', '011000001', '111000000', '010010001', '110010000',
    '011010000', '010000101', '110000100', '011000100', '010101000',
    '010100010', '010001010', '000101010', '010010100'];
/** FastReport's default wide-to-narrow ratio. */
const WIDE = 2;

const code39 = (value, calcCheckSum) => {
    const v = value.toUpperCase();
    let data = '';
    for (const c of v.split('')) {
        if (c === '*') continue;
        if (CODE39_CHARS.indexOf(c) < 0) return null;
        data += c;
    }
    let body = data;
    if (calcCheckSum) {
        let sum = 0;
        for (const c of body.split('')) sum += CODE39_CHARS.indexOf(c);
        body = body + CODE39_CHARS[sum % 43];
    }
    const full = '*' + body + '*';
    const e = newEncoded();
    for (let i = 0; i < full.length; i++) {
        const pattern = CODE39[CODE39_CHARS.indexOf(full[i])];
        for (let k = 0; k < 9; k++) e.widths.push(pattern[k] === '1' ? WIDE : 1);
        // Narrow inter-character gap (a space), except after the last one.
        if (i < full.length - 1) e.widths.push(1);
    }
    e.text = data;
    return e;
};

// -- Code128 ---------------------------------------------------------------------

const CODE128 = [
    '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
    '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
    '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
    '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
    '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
    '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
    '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
    '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
    '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
    '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
    '114131', '311141', '411131', '211412', '211214', '211232', '2331112'];
const START_B = 104;
const START_C = 105;
const CODE_B = 100;
const CODE_C = 99;
const STOP = 106;
const SET_B = 66; // 'B'
const SET_C = 67; // 'C'

/** Code set B for text, code set C for runs of four or more digits. */
const code128 = (value) => {
    if (value === '') return null;
    for (let k = 0; k < value.length; k++) {
        const c = value.charCodeAt(k);
        if (c < 32 || c > 126) return null;
    }
    const codes = [];
    let i = 0;
    let set = 0;
    while (i < value.length) {
        let run = digitRun(value, i);
        if (run >= 4) {
            // An odd run leaves its last digit for code set B.
            if (run % 2 === 1) run--;
            if (set !== SET_C) {
                codes.push(set === 0 ? START_C : CODE_C);
                set = SET_C;
            }
            for (let k = 0; k < run; k += 2) {
                codes.push(digitAt(value, i + k) * 10 + digitAt(value, i + k + 1));
            }
            i += run;
        } else {
            if (set !== SET_B) {
                codes.push(set === 0 ? START_B : CODE_B);
                set = SET_B;
            }
            codes.push(value.charCodeAt(i) - 32);
            i++;
        }
    }
    let sum = codes[0];
    for (let k = 1; k < codes.length; k++) sum += codes[k] * k;
    codes.push(sum % 103);
    codes.push(STOP);

    const e = newEncoded();
    for (const code of codes) {
        const p = CODE128[code];
        for (let k = 0; k < p.length; k++) e.widths.push(p.charCodeAt(k) - 48);
    }
    e.text = value;
    return e;
};

const digitRun = (s, from) => {
    let i = from;
    while (i < s.length && isDigit(s[i])) i++;
    return i - from;
};

// -- 2 of 5 interleaved ----------------------------------------------------------

const ITF = ['00110', '10001', '01001', '11000', '00101', '10100', '01100', '00011', '10010', '01010'];

const itf = (value, calcCheckSum) => {
    let digits = value.replace(/[^0-9]/g, '');
    if (digits === '') return null;
    if (calcCheckSum) {
        let sum = 0;
        for (let i = digits.length - 1, w = 3; i >= 0; i--, w = (w === 3 ? 1 : 3)) {
            sum += digitAt(digits, i) * w;
        }
        digits = digits + String.fromCharCode(48 + (10 - (sum % 10)) % 10);
    }
    if (digits.length % 2 === 1) digits = '0' + digits;
    const e = newEncoded();
    // Start: narrow bar, narrow space, narrow bar, narrow space.
    e.widths.push(1, 1, 1, 1);
    for (let i = 0; i < digits.length; i += 2) {
        const bars = ITF[digitAt(digits, i)];
        const spaces = ITF[digitAt(digits, i + 1)];
        for (let k = 0; k < 5; k++) {
            e.widths.push(bars[k] === '1' ? WIDE : 1);
            e.widths.push(spaces[k] === '1' ? WIDE : 1);
        }
    }
    // Stop: wide bar, narrow space, narrow bar.
    e.widths.push(WIDE, 1, 1);
    e.text = digits;
    return e;
};

// -- helpers ---------------------------------------------------------------------

const bitsToWidths = (bits, out) => {
    let i = 0;
    // EAN always starts with a bar ('1'); runs alternate from there.
    while (i < bits.length) {
        const c = bits[i];
        let j = i;
        while (j < bits.length && bits[j] === c) j++;
        out.push(j - i);
        i = j;
    }
};
